import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { collection, doc, onSnapshot } from 'firebase/firestore';

import * as auth from '../../services/auth';
import api from '../../services/api';
import { firestoreDb } from '../../services/firebase';
import { handleErrorNotification } from '../actions/notifications';
import { getUserDepots } from '../selectors/user';
import { addEnvironmentMode } from '../../models/depot';
import { PERMISSION_DENIED } from '../../constants/strings';

const initialState = {
  uid: '',
  email: '',
  error: null,
  photoURL: '',
  metadata: {},
  loading: false,
};

export const login = createAsyncThunk(
  'user/login',
  async (_, { rejectWithValue }) => {
    try {
      const loggedIn = await auth.login();
      if (!loggedIn) {
        return rejectWithValue(PERMISSION_DENIED);
      }
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const logout = createAsyncThunk(
  'user/logout',
  async (_, { dispatch, rejectWithValue }) => {
    try {
      await auth.logout();
      dispatch(setUserData(null));
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const configureUserDepot = createAsyncThunk(
  'user/configureUserDepot',
  async (_, { dispatch, getState }) => {
    const userDepots = getUserDepots(getState());
    try {
      const depotDoc = doc(collection(firestoreDb, 'config'), 'depot');
      onSnapshot(depotDoc, (snapshot) => {
        if (snapshot.exists() && userDepots?.length) {
          const depotEnvSettings = snapshot.data();
          dispatch(
            updateUserDepotData(
              addEnvironmentMode(depotEnvSettings, userDepots)
            )
          );
        }
      });
    } catch (error) {
      dispatch(handleErrorNotification(error));
    }
  }
);

export const fetchUserMetadata = createAsyncThunk(
  'user/fetchUserMetadata',
  async (id, { dispatch }) => {
    try {
      const {
        data: { user },
      } = await api.get(`/user/${id}`);
      dispatch(setUserMetaData(user));
      dispatch(configureUserDepot());
    } catch (error) {
      dispatch(handleErrorNotification(error));
    }
  }
);

export const onUserAuth = (user) => async (dispatch) => {
  if (user) {
    dispatch(
      setUserData({
        email: user.email,
        name: user.displayName,
        uid: user.uid,
        photoURL: user.photoURL,
      })
    );
    await dispatch(fetchUserMetadata(user.uid));
  }
};

const userSlice = createSlice({
  name: 'user',
  initialState,
  reducers: {
    setUserData(state, action) {
      return action.payload ? { ...action.payload } : initialState;
    },
    setUserMetaData(state, action) {
      state.metadata = action.payload;
    },
    updateUserDepotData(state, action) {
      state.metadata = {
        ...state.metadata,
        depotAccess: action.payload,
      };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(login.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(login.fulfilled, (state) => {
        state.loading = false;
        state.error = null;
      })
      .addCase(login.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(logout.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(logout.fulfilled, (state) => {
        state.loading = false;
        state.error = null;
      })
      .addCase(logout.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
    // .addCase(fetchUserMetadata.pending, (state) => {
    //   state.loading = true;
    //   state.error = null;
    // })
    // .addCase(fetchUserMetadata.fulfilled, (state) => {
    //   state.loading = false;
    // })
    // .addCase(fetchUserMetadata.rejected, (state, action) => {
    //   state.loading = false;
    //   state.error = action.payload;
    // });
  },
});

export const { setUserData, setUserMetaData, updateUserDepotData } =
  userSlice.actions;

export default userSlice.reducer;
